import axios from 'axios'
import { getSessionKey } from '../utils/session'

// No app key in the browser: every /api call lands on this site's own
// _ajax.php proxy, which re-signs the request with the server-side key
// (X-API-APP-ID 18) and forwards the visitor's real User-Agent for the API's
// bot detector. The browser only carries its session key.
const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
})

apiClient.interceptors.request.use((config) => {
	const key = getSessionKey()
	if (key) {
		config.headers = config.headers ?? {}
		;(config.headers as Record<string, string>)['X-SESSION-ID'] = key
	}
	return config
})

/**
 * Register the session once per browsing session, with full acquisition
 * context (akkubolt's registration payload — referrer + UTM + click-ids —
 * NOT akku-24's landing-page-only variant): the referrer drives the
 * organic/paid/direct classification, the click-ids drive campaign
 * attribution. Without these every akkumost session would land as "direct".
 *
 * Fire-and-forget by design: analytics must never delay or break a render.
 * Guarded by a module-level flag so React StrictMode's double-invoke cannot
 * double-register.
 */
let registered = false
export function registerVisitorSession(): void {
	if (registered) return
	registered = true
	if (typeof navigator !== 'undefined' && navigator.webdriver) return
	if (!getSessionKey()) return
	try {
		const payload: Record<string, string> = {
			landing_page_url: typeof window !== 'undefined' ? window.location.href : '',
		}
		if (typeof document !== 'undefined' && document.referrer) {
			payload.referrer_url = document.referrer
		}
		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search)
			for (const field of [
				'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
				'gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid', 'yclid', 'dclid', 'fbclid',
			]) {
				const value = params.get(field)
				if (value) payload[field] = value
			}
		}
		apiClient.post('/visitor_session', payload).catch(() => {
			/* analytics is best-effort */
		})
	} catch {
		/* never surface to the UI */
	}
}

export default apiClient
